import { collection, addDoc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logger } from '@/lib/logger';

// Mock meeting transcripts to simulate the "Zero-Click" ingestion
const MOCK_MEETINGS = [
    {
        title: "Acme Corp Initial Discovery",
        agent: "Travis S.",
        contact: "John Doe",
        company: "Acme Corp",
        transcript: "Hi John, thanks for joining. Let's discuss Acme's needs. We're looking for a CRM solution that can scale. That sounds great, we have exactly that. What is your budget? Around $15k per year. Perfect.",
        date: new Date(Date.now() - 1000 * 60 * 30), // 30 mins ago
    },
    {
        title: "Globex Enterprise Security Review",
        agent: "Sarah M.",
        contact: "Jane Smith",
        company: "Globex",
        transcript: "Hello Jane, let's talk security. We need SOC2 compliance. Oblique is fully SOC2 compliant and we encrypt all data at rest and in transit. That is wonderful to hear.",
        date: new Date(Date.now() - 1000 * 60 * 120), // 2 hours ago
    }
];

class AutoSyncService {
    constructor() {
        this.isSyncing = false;
        this.lastSync = null;
        this.syncInterval = null;
        this.listeners = new Set();
    }

    subscribe(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    notifyListeners(status) {
        for (const listener of this.listeners) {
            listener(status);
        }
    }

    startAutoSync(companyId) {
        if (this.syncInterval) return;
        
        // Run immediately
        this.sync(companyId);

        // Then every 30 minutes (simulated here as 5 minutes for demo purposes)
        this.syncInterval = setInterval(() => {
            this.sync(companyId);
        }, 5 * 60 * 1000);
    }

    stopAutoSync() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
            this.syncInterval = null;
        }
    }

    async sync(companyId) {
        if (!companyId || this.isSyncing) return;

        this.isSyncing = true;
        this.notifyListeners({ status: 'syncing', message: 'Syncing calendar and integrations...' });

        try {
            // Production behavior: sync is idle/up-to-date.
            // Dummy meeting injection into Firestore is disabled for production safety.
            this.lastSync = new Date();
            this.notifyListeners({ 
                status: 'idle', 
                message: 'All integrations up to date.',
                lastSync: this.lastSync,
                importedCount: 0
            });
        } catch (error) {
            logger.error("Auto-sync failed:", error);
            this.notifyListeners({ status: 'error', message: 'Sync failed.' });
        } finally {
            this.isSyncing = false;
        }
    }
}

export const autoSyncService = new AutoSyncService();
